import Postmate from 'postmate';
import { DefaultSdkConfiguratorOptions } from './constants/DefaultSdkConfiguratorOptions';
import { ConfiguratorOptions } from './types/ConfiguratorOptions';
import { ConfiguratorSdkEvents, ConfiguratorSdkOperations } from './types/configuratorSdkTypes';
import { FormattedOrderPayload } from './types/FormattedOrderPayload';
import { OrderPayloadSchema } from './types/OrderPayloadSchema';
import { generateConfiguratorUrl } from './util/generateConfiguratorUrl';

export interface SaveOptions {
  /**
   * The schema used to format {@link SavedEventProps.orderPayload}
   */
  payloadSchema: OrderPayloadSchema;
}

/**
 * Properties retured from a save operation
 */
export interface SavedEventProps {
  /**
   * A hash value that references the save
   */
  hash: string;

  /**
   * A map of images in base64 format from the save
   */
  images: { [id: string]: { width: number; height: number; src: string } };

  /**
   * The style description of the save
   */
  style: string;

  /**
   * A copy of the save formatted using the specified {@link SaveOptions.payloadSchema}
   */
  orderPayload: FormattedOrderPayload | undefined;
}

/**
 * Properties retured from a load operation
 */
export interface LoadedEventProps extends SavedEventProps {}

/**
 * Properties retured from a screenshot operation
 */
interface ScreenShotEventProps {
  /**
   * A base64 image
   */
  src: string;
}

/**
 * Properties retured on an error event
 */
interface ErrorEventProps {
  error: any;
}

/**
 * Method signature of a callback provided to the Snackbar
 */
export type SnackbarCallback = () => void;

/**
 * An embedded IdeaRoom Configurator
 */
class Configurator {
  private configuratorOptions: ConfiguratorOptions = DefaultSdkConfiguratorOptions;

  private api?: Postmate.ParentAPI;

  private snackbarCallbacks: { [actionNonce: string]: SnackbarCallback } = {};

  private timeout: number;

  /**
   * Triggered after a successful save operation.
   *
   * @event onSaved
   */
  public onSaved: ((props: SavedEventProps) => void) | undefined;

  /**
   * Triggered after a successful load operation.
   *
   * @event onLoaded
   */
  public onLoaded: ((props: LoadedEventProps) => void) | undefined;

  /**
   * Triggered after the client was successfully connected.
   *
   * @event onConnected
   */
  public onConnected: (() => void) | undefined;

  private onError: ((error: any) => void)[] = [];

  private onBuildingRendered: (() => void)[] = [];

  private onScreenshotSaved: ((image: ScreenShotEventProps) => void)[] = [];

  /**
   * @param configuratorUrl the URL to the subdomain where the configurator is hosted e.g. https://demo.carportview.com
   * @param container the ID value of a html element to embed the configurator in
   * @param configuratorOptions (optional) settings to initialize the configurator with
   */
  constructor(configuratorUrl: string, container: string, configuratorOptions?: Partial<ConfiguratorOptions>) {
    this.configuratorOptions = {
      ...this.configuratorOptions,
      ...configuratorOptions,
    };
    this.timeout = configuratorOptions?.timeout || 60000;

    const configuratorClassName = 'idearoom-embedded-configurator';

    const style = document.createElement('style');

    /* FIXME: initializeWithVisibleFrame was only created to workaround an issue with Postmate
      where it has a maximum wait time of 2.5 seconds for the handshake. When previewing changes from
      salesview, the V2 vendor server most of the time takes more than 2.5 seconds to return,
      which makes the iframe invisible forever. */
    const visibility = configuratorOptions && configuratorOptions.initializeWithVisibleFrame ? 'visible' : 'hidden';

    style.innerHTML = `.${configuratorClassName}{width:100%;height:100%;border:none;visibility:${visibility};}`;
    document.head.appendChild(style);

    const url = generateConfiguratorUrl(configuratorUrl, configuratorOptions);

    new Postmate({
      container: document.getElementById(container),
      url,
      name: configuratorClassName,
      classListArray: [configuratorClassName],
    })
      .then((parentApi: Postmate.ParentAPI) => {
        this.api = parentApi;

        this.api.on(ConfiguratorSdkEvents.Connected, this.onConnectedHandler.bind(this));
        this.api.on(ConfiguratorSdkEvents.Loaded, this.onLoadedHandler.bind(this));
        this.api.on(ConfiguratorSdkEvents.Saved, this.onSavedHandler.bind(this));
        this.api.on(ConfiguratorSdkEvents.SnackbarAction, this.onSnackbarAction.bind(this));
        this.api.on(ConfiguratorSdkEvents.BuildingRendered, this.onBuildingRenderedHandler.bind(this));
        this.api.on(ConfiguratorSdkEvents.ScreenshotSaved, this.onScreenshotSavedHandler.bind(this));
        this.api.on(ConfiguratorSdkEvents.Error, this.onErrorHandler.bind(this));
      })
      .catch(() => {
        this.onErrorHandler({
          error: new Error('Failed to acquire connection with the Configurator.'),
        });
      });
  }

  /**
   * Saves the current building. {@link onSaved} will be triggered upon successful completion.
   * @param saveOptions (optional) settings to control how saves work
   */
  save(saveOptions: SaveOptions): void {
    this.log('save');

    if (this.api) {
      const { payloadSchema = OrderPayloadSchema.Default } = saveOptions || {};

      if (!Object.values(OrderPayloadSchema).includes(payloadSchema)) {
        console.error(
          `Configurator.save (skipped) - unexpected value for saveOptions.payloadSchema: ${payloadSchema}. Expected one of: [${Object.values(
            OrderPayloadSchema,
          ).join(', ')}].`,
        );
        return;
      }

      this.api.call(ConfiguratorSdkOperations.Save, { payloadSchema });
    }
  }

  /**
   * Loads a previously saved building. {@link onLoaded} will be triggered upon successful completion.
   *
   * @param hash a hash value that references a previous save
   * @param uuid (optional) a uuid value that references a previous save
   */
  load(hash: string, uuid?: string): void {
    this.log('load', hash);

    if (this.api && hash) {
      this.api.call(ConfiguratorSdkOperations.Load, { hash, uuid });
    }
  }

  /**
   * Displays a Snackbar message in the configurator.
   *
   * For a Snackbar message to have an action both `actionLabel` and `actionCallback` must be set.
   *
   * @param message the message to display
   * @param autoHideDuration (optional) the duration for the snackbar to display in ms unless dismissed
   * @param actionLabel (optional) a label to display as the action when used with `actionCallback`
   * @param actionCallback (optional) a callback to perform when the `actionLabel` is clicked
   */
  snackbar(message: string, autoHideDuration?: number, actionLabel?: string, actionCallback?: SnackbarCallback): void {
    this.log('snackbar', message, autoHideDuration, actionLabel);

    const actionNonce = `snackbarActionNonce_${Date.now()}`;

    if (actionLabel && actionCallback) {
      this.snackbarCallbacks[actionNonce] = actionCallback;
    }

    if (this.api) {
      this.api.call(ConfiguratorSdkOperations.Snackbar, {
        message,
        autoHideDuration,
        actionLabel,
        actionNonce,
      });
    }
  }

  /**
   * Closes the Location/Zip/State dialog if open.
   */
  closeLocationDialog() {
    if (this.api) {
      this.api.call(ConfiguratorSdkOperations.CloseLocationDialog);
    }
  }

  /**
   * Changes the selected style in the configurator.
   *
   * @param styleKey The style key
   * @returns A promise that is resolved once the style successfully changed.
   */
  async changeStyle(styleKey: string) {
    if (this.api) {
      const promise = new Promise<void>((resolve, reject) => {
        let errorListener: Parameters<Configurator['addOnErrorListener']>[0]; // eslint-disable-line prefer-const
        const timeout = setTimeout(
          () => errorListener(new Error('Configurator.changeStyle has timed out.')),
          this.timeout,
        );

        const listener = () => {
          resolve();
          this.removeOnBuildingRenderedListener(listener);
          this.removeOnErrorListener(errorListener);
          clearTimeout(timeout);
        };
        errorListener = (error: any) => {
          reject(error);
          this.removeOnBuildingRenderedListener(listener);
          this.removeOnErrorListener(errorListener);
          clearTimeout(timeout);
        };
        this.addOnBuildingRenderedListener(listener);
        this.addOnErrorListener(errorListener);
      });

      this.api.call(ConfiguratorSdkOperations.ChangeStyle, {
        styleKey,
      });

      return promise;
    }
    throw new Error(`Configurator.changeStyle method called before the Idearoom configurator was loaded!`);
  }

  /**
   * Changes the selected structure supplier in the configurator.
   *
   * @param supplierKey The supplier key
   * @returns A promise that is resolved once the supplier successfully changed.
   */
  async changeStructureSupplier(supplierKey: string) {
    if (this.api) {
      const promise = new Promise<void>((resolve, reject) => {
        let errorListener: Parameters<Configurator['addOnErrorListener']>[0]; // eslint-disable-line prefer-const
        const timeout = setTimeout(
          () => errorListener(new Error('Configurator.changeStructureSupplier has timed out.')),
          this.timeout,
        );

        const listener = () => {
          resolve();
          this.removeOnBuildingRenderedListener(listener);
          this.removeOnErrorListener(errorListener);
          clearTimeout(timeout);
        };
        errorListener = (error: any) => {
          reject(error);
          this.removeOnBuildingRenderedListener(listener);
          this.removeOnErrorListener(errorListener);
          clearTimeout(timeout);
        };
        this.addOnBuildingRenderedListener(listener);
        this.addOnErrorListener(errorListener);
      });

      this.api.call(ConfiguratorSdkOperations.ChangeStructureSupplier, {
        supplierKey,
      });

      return promise;
    }
    throw new Error(`Configurator.changeStructureSupplier method called before the Idearoom configurator was loaded!`);
  }

  /**
   * Renders and return a screenshot of an option. Utility used for creating door/siding/roof icons.
   *
   * @param optionType the option type that will be rendered
   * @param optionKey the option key
   * @param width screenshot width
   * @param height screenshot height
   * @param hideWatermark hide watermarks on the screenshot
   * @param zoomToFit adjust camera zoom before taking the screenshot
   * @param turnOffShadows turn off the building shadows
   * @param cameraRotation the camera rotation used for taking the screenshot
   * @returns A promise that is resolved with the image as a base64 string
   */
  async saveOptionScreenshot(
    optionType: string, // Not using OptionType because its the only @/types import
    optionKey: string,
    width: number,
    height: number,
    hideWatermark: boolean,
    zoomToFit?: number,
    turnOffShadows?: boolean,
    cameraRotation?: { x: number; y: number; z: number },
  ) {
    if (this.api) {
      const promise = new Promise<ScreenShotEventProps>((resolve, reject) => {
        let errorListener: Parameters<Configurator['addOnErrorListener']>[0]; // eslint-disable-line prefer-const
        const timeout = setTimeout(
          () => errorListener(new Error('Configurator.saveOptionScreenshot has timed out.')),
          this.timeout,
        );

        const listener = (image: ScreenShotEventProps) => {
          resolve(image);
          this.onScreenshotSaved.splice(this.onScreenshotSaved.indexOf(listener));
          this.removeOnErrorListener(errorListener);
          clearTimeout(timeout);
        };
        errorListener = (error: any) => {
          reject(error);
          this.onScreenshotSaved.splice(this.onScreenshotSaved.indexOf(listener));
          this.removeOnErrorListener(errorListener);
          clearTimeout(timeout);
        };
        this.onScreenshotSaved.push(listener);
        this.addOnErrorListener(errorListener);
      });

      this.api.call(ConfiguratorSdkOperations.SaveOptionScreenshot, {
        optionType,
        optionKey,
        width,
        height,
        hideWatermark,
        zoomToFit,
        turnOffShadows,
        cameraRotation,
      });

      return promise;
    }
    throw new Error(`Configurator.SaveOptionScreenshot method called before the Idearoom configurator was loaded!`);
  }

  /**
   * Saves a screenshot of the building.
   *
   * @param width screenshot width
   * @param height screenshot height
   * @param hideWatermark hide watermarks on the screenshot
   * @param zoomToFit adjust camera zoom before taking the screenshot
   * @param turnOffShadows turn off the building shadows
   * @param cameraRotation the camera rotation used for taking the screenshot
   * @returns A promise that is resolved with the image as a base64 string
   */
  async saveScreenshot(
    width: number,
    height: number,
    hideWatermark: boolean,
    zoomToFit?: number,
    turnOffShadows?: boolean,
    cameraRotation?: { x: number; y: number; z: number },
  ) {
    if (this.api) {
      const promise = new Promise<ScreenShotEventProps>((resolve, reject) => {
        let errorListener: Parameters<Configurator['addOnErrorListener']>[0]; // eslint-disable-line prefer-const
        const timeout = setTimeout(
          () => errorListener(new Error('Configurator.saveScreenshot has timed out.')),
          this.timeout,
        );

        const listener = (image: ScreenShotEventProps) => {
          resolve(image);
          this.onScreenshotSaved.splice(this.onScreenshotSaved.indexOf(listener));
          this.removeOnErrorListener(errorListener);
          clearTimeout(timeout);
        };
        errorListener = (error: any) => {
          reject(error);
          this.onScreenshotSaved.splice(this.onScreenshotSaved.indexOf(listener));
          this.removeOnErrorListener(errorListener);
          clearTimeout(timeout);
        };
        this.onScreenshotSaved.push(listener);
        this.addOnErrorListener(errorListener);
      });

      this.api.call(ConfiguratorSdkOperations.SaveScreenshot, {
        width,
        height,
        hideWatermark,
        zoomToFit,
        turnOffShadows,
        cameraRotation,
      });

      return promise;
    }
    throw new Error(`Configurator.saveScreenshot method called before the Idearoom configurator was loaded!`);
  }

  /**
   * Adds a listener that is triggered when the configurator throws an error
   *
   * @event onError
   * @param method
   */
  addOnErrorListener(method: (error: any) => void) {
    this.onError.push(method);
  }

  /**
   * Removes the onError listener
   *
   * @event onError
   * @param method
   */
  removeOnErrorListener(method: (error: any) => void) {
    this.onError.splice(this.onError.indexOf(method));
  }

  /**
   * Adds a listener that is triggered when the building is rendered after an update.
   *
   * @event onBuildingRendered
   * @param method
   */
  addOnBuildingRenderedListener(method: () => void) {
    this.onBuildingRendered.push(method);
  }

  /**
   * Removes a building rendered listener.
   *
   * @event onBuildingRendered
   * @param method
   */
  removeOnBuildingRenderedListener(method: () => void) {
    this.onBuildingRendered.splice(this.onBuildingRendered.indexOf(method));
  }

  /**
   * Destroys the embedded configurator iframe.
   * Use it to clear resources.
   */
  destroy() {
    if (this.api) {
      this.api.destroy();
    }
  }

  private onSnackbarAction({ actionNonce }: { actionNonce: string }): void {
    if (this.snackbarCallbacks[actionNonce]) {
      this.snackbarCallbacks[actionNonce]();
      delete this.snackbarCallbacks[actionNonce];
    }
  }

  private onConnectedHandler(): void {
    this.log('onConnectedHandler');

    if (this.api) {
      this.api.frame.style.visibility = 'visible';
      this.api.call(ConfiguratorSdkOperations.Initialize, this.configuratorOptions);
    }

    if (this.onConnected) {
      this.onConnected();
    }
  }

  private onSavedHandler(props: SavedEventProps): void {
    this.log('onSavedHandler', props);

    if (this.onSaved) {
      this.onSaved(props);
    }
  }

  private onLoadedHandler(props: LoadedEventProps): void {
    this.log('onLoadedHandler', props);

    if (this.onLoaded) {
      this.onLoaded(props);
    }
  }

  private onErrorHandler(props: ErrorEventProps): void {
    this.log('onErrorHandler', props);

    this.onError.forEach((listener) => listener(props.error));
  }

  private onBuildingRenderedHandler(): void {
    this.log('onBuildingRenderedHandler');

    this.onBuildingRendered.forEach((listener) => listener());
  }

  private onScreenshotSavedHandler(image: ScreenShotEventProps): void {
    this.log('onScreenshotSavedHandler', image);

    this.onScreenshotSaved.forEach((listener) => listener(image));
  }

  private log(message: string, ...optionalParams: any[]): void {
    if (this.configuratorOptions && this.configuratorOptions.verbose) {
      // eslint-disable-next-line no-console
      console.log(`IdeaRoom Configurator SDK: ${message}`, ...optionalParams);
    }
  }
}

export { Configurator };
